import React, { useState } from 'react';
import { X, Upload, AlertCircle, CheckCircle } from 'lucide-react';
import Papa from 'papaparse';

export default function ImportModal({ onClose, onImport, existingFirms }) {
    const [file, setFile] = useState(null);
    const [parsedData, setParsedData] = useState(null);
    const [errors, setErrors] = useState([]);
    const [importing, setImporting] = useState(false);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        if (!selectedFile.name.endsWith('.csv')) {
            setErrors(['Please upload a CSV file']);
            return;
        }

        setFile(selectedFile);
        setErrors([]);
        setParsedData(null);

        Papa.parse(selectedFile, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                validateAndParse(results.data);
            },
            error: (error) => {
                setErrors([`Failed to parse CSV: ${error.message}`]);
            }
        });
    };

    const validateAndParse = (data) => {
        const validationErrors = [];
        const validFirms = [];

        const requiredFields = ['Firm Name'];
        
        data.forEach((row, index) => {
            const rowNum = index + 2;

            if (!row['Firm Name'] || !row['Firm Name'].trim()) {
                validationErrors.push(`Row ${rowNum}: Firm Name is required`);
                return;
            }

            const firmName = row['Firm Name'].trim();
            const existingFirmNames = existingFirms.map(f => f.name.toLowerCase());
            if (existingFirmNames.includes(firmName.toLowerCase())) {
                validationErrors.push(`Row ${rowNum}: Firm "${firmName}" already exists`);
                return;
            }

            if (validFirms.some(f => f.name.toLowerCase() === firmName.toLowerCase())) {
                validationErrors.push(`Row ${rowNum}: Duplicate firm "${firmName}" in CSV`);
                return;
            }

            const firmData = {
                name: firmName,
                founded: parseInt(row['Year Founded']) || new Date().getFullYear(),
                totalEmployees: parseInt(row['Total Employees']) || 0,
                strategies: row['Investment Strategies'] 
                    ? row['Investment Strategies'].split(';').map(s => s.trim()).filter(Boolean)
                    : [],
                offices: parseOffices(row),
                payoutStructure: row['Payout Structure'] || '',
                bonusDate: row['Bonus Date'] || '',
                aum: row['AUM'] || '',
                notes: row['Notes'] || ''
            };

            validFirms.push(firmData);
        });

        if (validationErrors.length > 0) {
            setErrors(validationErrors);
            setParsedData(null);
        } else {
            setErrors([]);
            setParsedData(validFirms);
        }
    };

    const parseOffices = (row) => {
        const cities = row['Office Cities'] ? row['Office Cities'].split(';').map(s => s.trim()).filter(Boolean) : [];
        const countries = row['Office Countries'] ? row['Office Countries'].split(';').map(s => s.trim()).filter(Boolean) : [];
        const hq = row['Headquarters'] ? row['Headquarters'].trim() : '';

        if (cities.length === 0) {
            return [{ city: '', country: '', isPrimary: true }];
        }

        return cities.map((city, index) => ({
            city: city,
            country: countries[index] || '',
            isPrimary: city === hq
        }));
    };

    const handleImport = async () => {
        if (!parsedData || parsedData.length === 0) return;

        setImporting(true);
        try {
            await onImport(parsedData);
        } catch (error) {
            setErrors([`Import failed: ${error.message}`]);
        } finally {
            setImporting(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                    <h2 className="font-semibold text-gray-900">Import Firms from CSV</h2>
                    <button onClick={onClose} className="p-1 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="p-6">
                    <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Select CSV File
                        </label>
                        <div className="flex items-center gap-4">
                            <input
                                type="file"
                                accept=".csv"
                                onChange={handleFileChange}
                                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                            />
                        </div>
                        <p className="mt-2 text-xs text-gray-500">
                            Required columns: Firm Name. Optional: Year Founded, Total Employees, Investment Strategies, Office Cities, Office Countries, Headquarters, Payout Structure, Bonus Date, AUM, Notes
                        </p>
                    </div>

                    {errors.length > 0 && (
                        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                <div className="flex-1">
                                    <h3 className="text-sm font-semibold text-red-800 mb-2">
                                        Validation Errors - Import Rejected
                                    </h3>
                                    <ul className="text-sm text-red-700 space-y-1">
                                        {errors.map((error, index) => (
                                            <li key={index}>• {error}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {parsedData && parsedData.length > 0 && (
                        <div className="mb-6">
                            <div className="flex items-start gap-2 p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                                <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <h3 className="text-sm font-semibold text-green-800">
                                        Valid Data - Ready to Import
                                    </h3>
                                    <p className="text-sm text-green-700 mt-1">
                                        {parsedData.length} firm{parsedData.length !== 1 ? 's' : ''} will be added to the database
                                    </p>
                                </div>
                            </div>

                            <h3 className="text-sm font-semibold text-gray-900 mb-3">Preview:</h3>
                            <div className="border border-gray-200 rounded-lg overflow-hidden max-h-96 overflow-y-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Firm Name</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Founded</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Employees</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Strategies</th>
                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Offices</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {parsedData.map((firm, index) => (
                                            <tr key={index}>
                                                <td className="px-4 py-2 text-sm text-gray-900">{firm.name}</td>
                                                <td className="px-4 py-2 text-sm text-gray-900">{firm.founded}</td>
                                                <td className="px-4 py-2 text-sm text-gray-900">{firm.totalEmployees}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500">{firm.strategies.join(', ')}</td>
                                                <td className="px-4 py-2 text-sm text-gray-500">
                                                    {firm.offices.map(o => o.city).filter(Boolean).join(', ')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>

                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleImport}
                        disabled={!parsedData || parsedData.length === 0 || importing}
                        className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        <Upload className="w-4 h-4" />
                        {importing ? 'Importing...' : `Import ${parsedData?.length || 0} Firms`}
                    </button>
                </div>
            </div>
        </div>
    );
}